import { trace } from '@opentelemetry/api';

/**
 * 属性の型と命名規約（docs/spec/observability.md §5、§7）。
 *
 * アプリ固有の属性は `class.<対象>.<属性>` の snake_case に限る。
 * 値は列挙・数値・コンテンツ ID だけにする。自由文、学習者のコード・入出力、
 * 判定から導いた学習状態（正答率、連続正解数、Leitner の箱など）は型にも許可リストにも入れない。
 */

export type Verdict = 'AC' | 'WA' | 'RE' | 'TLE' | 'CE';

/** card-schema.md §3 の `type`。 */
export type CardKind =
  | 'predict-output'
  | 'fill-blank'
  | 'parsons'
  | 'choice'
  | 'reduce'
  | 'complexity-quiz'
  | 'write-ac'
  | 'viz-step'
  | 'memo';

export type GradeMethod = 'local' | 'sandbox';

/** problem: 問題の作業台（例題・類題の実行と採点。problem-format.md §7）。 */
export type RunPurpose = 'card' | 'lab' | 'playground' | 'lesson' | 'problem';

/** 通信・実行基盤から見た結果（運用向け）。判定そのものは class.verdict に持つ。 */
export type RunOutcome =
  | 'completed'
  | 'compile_error'
  | 'runtime_error'
  | 'timeout'
  | 'rate_limited'
  | 'busy'
  | 'gateway_timeout'
  | 'http_error'
  | 'network_error';

export type SpanName =
  | 'class.card.grade'
  | 'class.run'
  | 'class.fmt'
  | 'class.lab.check'
  | 'class.view.change'
  | 'class.lesson.issue';

/**
 * レッスンの描画で起きたこと（lesson-format.md §5.3、§5.4）。値は列挙とノードの型名だけ。
 * - unsupported_node: LessonDoc に未知の型が来て描かなかった
 * - link_rejected: 描画側の lessonLinkTarget がリンクを拒否して文字だけを描いた
 */
export type LessonIssueKind = 'unsupported_node' | 'link_rejected';

interface ClassAttributeMap {
  'class.card.id': string;
  'class.card.kind': CardKind;
  'class.unit.id': string;
  'class.lab.id': string;
  'class.lab.action': string;
  'class.view.route': string;
  'class.lesson.id': string;
  'class.lesson.issue': string;
  /** 問題 id（problem-format.md §2.1。例 abc123-b）。 */
  'class.problem.id': string;
  /** 類題の id（例題の実行では付けない）。 */
  'class.problem.exercise': string;
  'class.run.purpose': RunPurpose;
  'class.run.cases': number;
  'class.run.code_bytes': number;
  'class.run.outcome': RunOutcome;
  'class.grade.method': GradeMethod;
  'class.verdict': Verdict;
  // 計算量メーター（docs/spec/observability.md §6.1。値は実験 id・N・件数のみ。学習者状態は入れない）
  'class.meter.experiment': string;
  'class.meter.variant': string;
  'class.meter.mode': string;
  'class.meter.max_n': number;
  'class.meter.sizes': number;
  'class.meter.skipped': number;
  'class.meter.timed_out': boolean;
}

export type ClassAttributeKey = keyof ClassAttributeMap;
export type ClassAttributes = Partial<ClassAttributeMap>;

/** span に付けてよい属性名の一覧（§5 の ClassAttributes と、標準の `error.type`）。 */
export const CLASS_ATTRIBUTE_KEYS: readonly ClassAttributeKey[] = [
  'class.card.id',
  'class.card.kind',
  'class.unit.id',
  'class.lab.id',
  'class.lab.action',
  'class.view.route',
  'class.lesson.id',
  'class.lesson.issue',
  'class.problem.id',
  'class.problem.exercise',
  'class.run.purpose',
  'class.run.cases',
  'class.run.code_bytes',
  'class.run.outcome',
  'class.grade.method',
  'class.verdict',
  'class.meter.experiment',
  'class.meter.variant',
  'class.meter.mode',
  'class.meter.max_n',
  'class.meter.sizes',
  'class.meter.skipped',
  'class.meter.timed_out',
];

const allowed: ReadonlySet<string> = new Set<string>(CLASS_ATTRIBUTE_KEYS);

/** §7 の 2: `class.` 名前空間、ドット区切り、小文字 snake_case。 */
export const CLASS_ATTRIBUTE_NAME = /^class(\.[a-z][a-z0-9_]*)+$/;

/**
 * 許可リストにある属性だけを残す。型を迂回して渡された未知のキーはここで落とす。
 */
export function sanitizeAttributes(attrs: ClassAttributes): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(attrs)) {
    if (!allowed.has(key)) continue;
    if (typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value))) {
      out[key] = value;
    }
  }
  return out;
}

/** `class.lesson.issue` の値。ノードの型名は英字だけを残す（自由文を入れない）。 */
export function lessonIssueValue(kind: LessonIssueKind, nodeType?: string): string {
  const t = (nodeType ?? '').replace(/[^A-Za-z]/g, '').slice(0, 40);
  return t === '' ? kind : `${kind}.${t}`;
}

/**
 * レッスンの描画の異常を 1 件記録する（lesson-format.md §5.3 の `lesson.unsupported_node`）。
 * 短い span `class.lesson.issue` に `class.lesson.id` と `class.lesson.issue` を付けて閉じる。
 * テレメトリが無効なら @opentelemetry/api の no-op 実装で何も送らない。
 */
export function recordLessonIssue(
  lessonId: string,
  kind: LessonIssueKind,
  nodeType?: string,
): void {
  const attrs = sanitizeAttributes({
    'class.lesson.id': lessonId,
    'class.lesson.issue': lessonIssueValue(kind, nodeType),
  });
  trace.getTracer('inductor-class').startSpan('class.lesson.issue', { attributes: attrs }).end();
}
